/**
 * One sampling policy for every Sentry SDK in the repo - browser, Netlify
 * functions, the Trigger.dev worker and the mobile app.
 *
 * Non-production environments (local `bun dev`, deploy previews, staging, EAS
 * `development`/`preview` builds) share the production quota, and they are
 * noisier than production: every hot reload re-runs the same code paths, and
 * `consoleLoggingIntegration` forwards `log`/`info` as well. Left at
 * production rates they exhaust the quota in days.
 *
 * So outside production everything is pinned to {@link NON_PRODUCTION_SAMPLE_RATE}
 * and structured logs are off. A caller-supplied `tracesSampleRate` (the
 * `*_SENTRY_TRACES_SAMPLE_RATE` env vars) is honoured in production but
 * *clamped* outside it, so a stale `.env` cannot quietly reopen the firehose.
 */

/** Fraction of events kept outside production. */
export const NON_PRODUCTION_SAMPLE_RATE = 0.01;

export type SentrySamplingPolicy = {
  /** Fraction of error events sent. */
  sampleRate: number;
  /** Fraction of transactions traced. */
  tracesSampleRate: number;
  /** Fraction of sessions recorded by Session Replay (browser only). */
  replaysSessionSampleRate: number;
  /** Fraction of errored sessions recorded by Session Replay (browser only). */
  replaysOnErrorSampleRate: number;
  /** Whether structured logs (and console forwarding) are sent at all. */
  enableLogs: boolean;
};

type SentrySamplingParams = {
  environment?: string;
  /** Requested traces rate, e.g. from `SENTRY_TRACES_SAMPLE_RATE`. */
  tracesSampleRate?: number;
};

const PRODUCTION_ENVIRONMENTS = new Set(['production', 'prod']);

/**
 * Only an explicit production environment counts. An unset environment is
 * treated as non-production - the cheap side is the safe default.
 */
export function isProductionEnvironment(environment?: string): boolean {
  return PRODUCTION_ENVIRONMENTS.has((environment ?? '').trim().toLowerCase());
}

export function sentrySamplingPolicy({
  environment,
  tracesSampleRate,
}: SentrySamplingParams = {}): SentrySamplingPolicy {
  const requested = Number.isFinite(tracesSampleRate) ? tracesSampleRate : undefined;

  if (!isProductionEnvironment(environment)) {
    return {
      sampleRate: NON_PRODUCTION_SAMPLE_RATE,
      tracesSampleRate: Math.min(
        requested ?? NON_PRODUCTION_SAMPLE_RATE,
        NON_PRODUCTION_SAMPLE_RATE
      ),
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: NON_PRODUCTION_SAMPLE_RATE,
      enableLogs: false,
    };
  }

  return {
    sampleRate: 1,
    tracesSampleRate: requested ?? 0.1,
    replaysSessionSampleRate: 0.01,
    replaysOnErrorSampleRate: 1,
    enableLogs: true,
  };
}
